import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fingerprint, ArrowRight, ShieldCheck } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { BIOMETRIC_DEVICE_CONFIG } from "../../constants/api";

/**
 * Biometric readers, as an integration alongside ERP and eMedHub.
 *
 * Follows the eMedHub split: the connection is reported here, the day-to-day
 * work (devices, employee mapping) is its own screen. The connection itself
 * is set per deployment and is not editable here — a customer who can
 * redirect it can silently break the whole attendance feed.
 */
const ConnectBiometric = () => {
    const { fetch } = useApiMethods();
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    const allowed = !!checkUserPermissionFunc("biometric", "biometric_devices", "SubMenu")?.permission;

    const load = useCallback(async () => {
        if (!allowed) { setLoading(false); return; }
        try {
            const res = await fetch(BIOMETRIC_DEVICE_CONFIG);
            setConfig(res?.data || null);
        } catch (_) {
            setConfig(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowed]);

    useEffect(() => { load(); }, [load]);

    if (!allowed) {
        return (
            <div className="max-w-3xl mt-8 border-t border-gray-100 dark:border-gray-700 pt-8">
                <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        You don't have permission to manage the biometric integration.
                    </p>
                </div>
            </div>
        );
    }

    const configured = !!config?.connection?.configured;
    const deviceCount = config?.device_count ?? 0;

    return (
        <div className="max-w-3xl space-y-4 mt-8 border-t border-gray-100 dark:border-gray-700 pt-8">
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                    <Fingerprint size={22} />
                </div>
                <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Biometric Readers
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Fingerprint and face readers that post punches into attendance.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="h-4 w-56 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
            ) : (
                <>
                    <div className="flex flex-wrap gap-2 text-xs">
                        <span className={`rounded-full px-2.5 py-1 border ${configured
                            ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
                            : "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"}`}>
                            Connection: {configured ? "Configured" : "Not configured"}
                        </span>
                        <span className="rounded-full px-2.5 py-1 border bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700">
                            {deviceCount} device{deviceCount === 1 ? "" : "s"}
                        </span>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        {configured
                            ? "The reader server is connected. Devices and employee mappings are managed on their own screen."
                            : "No reader server is connected for your organisation. Contact support to set one up — the connection is configured per deployment."}
                    </p>

                    <Link
                        to="/settings/integrations/biometric"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-sidebar-primary hover:underline"
                    >
                        Manage devices and employee mapping
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </>
            )}
        </div>
    );
};

export default ConnectBiometric;
